import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Clock, CheckCircle, Plus, Briefcase, Calendar, MapPin, X } from 'lucide-react';
import Header from '../common/Header';
import NotificationPrompt from '../common/NotificationPrompt';
import InstallPwaPrompt from '../common/InstallPwaPrompt';
import CreateJobForm from './CreateJobForm';
import EmployerJobDetails from './EmployerJobDetails';
import { createJob } from '../../services/jobService';
import { syncFcmToken } from '../../config/messaging';
import { getAuth, deleteUser } from 'firebase/auth';

const BRAND_COLORS = {
  primary: '#1a1a1a',
  accent: '#C9A961',
  light: '#F5F5F0',
  lightGray: '#E8E8E3',
};

const EmployerHomePage = ({ user, userProfile }) => {
  const [employerData, setEmployerData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('home');
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [showVerifiedBanner, setShowVerifiedBanner] = useState(true);

  const [creatingJob, setCreatingJob] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!user) return;

    const fetchEmployerData = async () => {
      try {
        const empDoc = await getDoc(doc(db, 'employers', user.uid));
        if (empDoc.exists()) {
          setEmployerData(empDoc.data());
        }
      } catch (error) {
        console.error('Error fetching employer data:', error);
      }
    };

    fetchEmployerData();
    // Sync FCM token silently
    syncFcmToken(user.uid, 'employers');

    // Check if verified banner was dismissed
    const dismissed = localStorage.getItem(`skillvend_employer_verified_banner_dismissed_${user.uid}`);
    if (dismissed === 'true') {
      setShowVerifiedBanner(false);
    }

    // Real-time listener for Employer's Jobs
    const jobsQuery = query(
      collection(db, 'jobs'),
      where('employerId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(jobsQuery, (snapshot) => {
      const employerJobs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setJobs(employerJobs);
      setLoading(false);
    }, (error) => {
      console.error('Error in jobs listener:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const handleDismissVerifiedBanner = () => {
    setShowVerifiedBanner(false);
    localStorage.setItem(`skillvend_employer_verified_banner_dismissed_${user.uid}`, 'true');
  };

  // Handle deep linking for job details
  useEffect(() => {
    if (!loading && jobs.length > 0) {
      const jobId = searchParams.get('jobId');
      if (jobId) {
        const jobToSelect = jobs.find(j => j.id === jobId);
        if (jobToSelect) {
          setSelectedJob(jobToSelect);
          // Optional: Clear the param so it doesn't persist on refresh if desired, 
          // but keeping it allows sharing/refreshing to same state.
          // For now, let's keep it or clear it? User didn't specify.
          // Let's clear it to avoid re-opening if they close and refresh.
          setSearchParams({});
        }
      }
    }
  }, [loading, jobs, searchParams, setSearchParams]);

  const handleCreateJob = async (jobData) => {
    setCreatingJob(true);
    try {
      // Inject employer location into job data
      const jobWithLocation = {
        ...jobData,
        location: {
          address: employerData?.location?.addressFormatted || '',
          lat: employerData?.location?.lat || 0,
          lng: employerData?.location?.lng || 0,
        },
        employerPhone: employerData?.phoneNumber || '', // Inject employer phone number
        employerName: employerData?.displayName || '' // Inject employer name
      };

      await createJob(jobWithLocation, user.uid);
      // Jobs list will automatically update via onSnapshot listener
      setShowCreateJob(false);
      // Optional: Show success notification
    } catch (error) {
      console.error('Error creating job:', error);
      // Optional: Show error notification
    } finally {
      setCreatingJob(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm("Are you sure you want to delete your account? This action is permanent and cannot be undone.")) {
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (currentUser) {
          await deleteUser(currentUser);
          // Redirect handled by onAuthStateChanged typically, but explicit here is fine
        }
      } catch (error) {
        console.error("Error deleting account:", error);
        if (error.code === 'auth/requires-recent-login') {
          alert("For security reasons, please log out and log back in to verify your identity before deleting your account.");
        } else {
          alert("Failed to delete account. Please try again.");
        }
      }
    }
  };

  const renderContent = () => {
    switch (currentView) {
      case 'home':
        return renderDashboard();
      case 'bookings':
        return renderBookings();
      case 'profile':
        return renderProfile();
      default:
        return renderDashboard();
    }
  };

  const renderBookings = () => {
    const completedJobs = jobs.filter(job => job.status === 'completed');

    return (
      <div className="mb-12">
        <h1 className="text-4xl font-light tracking-wide mb-6" style={{ color: BRAND_COLORS.primary }}>
          Completed Bookings
        </h1>

        {completedJobs.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 p-12 text-center rounded-lg">
            <CheckCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No completed bookings</h3>
            <p className="text-gray-500 font-light">Jobs marked as completed will appear here.</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {completedJobs.map((job) => (
              <div key={job.id} className="bg-white border border-gray-200 p-6 rounded-lg hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-medium text-gray-900 mb-1">{job.title}</h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Calendar size={14} className="mr-1" />
                        {job.date}
                      </span>
                      <span className="flex items-center">
                        <Clock size={14} className="mr-1" />
                        {job.time} ({job.duration}h)
                      </span>
                    </div>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-medium capitalize bg-green-100 text-green-800">
                    {job.status}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">{job.description}</p>
                <div className="flex justify-between items-center text-sm">
                  <div className="text-gray-500">
                    Budget: ₹{job.budget?.value}
                  </div>
                  <button
                    onClick={() => setSelectedJob(job)}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDashboard = () => {
    const isVerified = employerData?.verificationStatus === 'verified';
    const isDeactivated = employerData?.deactivatedByAdmin === true;

    return (
      <>
        {/* Header */}
        <div className="mb-8 md:mb-12 flex flex-col md:flex-row md:justify-between md:items-end gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-light tracking-wide mb-2" style={{ color: BRAND_COLORS.primary }}>
              Welcome, {employerData?.displayName || 'Employer'}
            </h1>
            <p className="text-gray-600 font-light">Your SkillVend Dashboard</p>
          </div>
          {isVerified && !isDeactivated && (
            <button
              onClick={() => setShowCreateJob(true)}
              className="w-full md:w-auto px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 flex items-center justify-center space-x-2 transition-colors shadow-md md:shadow-none"
            >
              <Plus size={20} />
              <span>Post New Job</span>
            </button>
          )}
        </div>

        {/* Notification Prompt */}
        <NotificationPrompt
          userId={user.uid}
          userType="employer"
          message="Stay updated with real-time notifications when qualified professionals match your requirements. Enable notifications to never miss the perfect candidate for your opportunities."
        />

        {/* PWA Install Prompt */}
        <InstallPwaPrompt userId={user.uid} />

        {/* Deactivation Banner */}
        {isDeactivated && (
          <div className="bg-red-50 border-2 border-red-200 p-8 md:p-10 mb-8">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 border-2 border-red-300 rounded-full flex items-center justify-center bg-red-100">
                  <X className="w-8 h-8 text-red-600" />
                </div>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-light tracking-wide mb-4 text-red-800">
                  Account Deactivated
                </h2>
                <p className="text-red-700 font-medium mb-2">
                  You cannot create jobs, please contact admin to enable the same.
                </p>
                {employerData?.adminDeactivationReason && (
                  <p className="text-red-600 font-light">
                    <strong>Reason:</strong> {employerData.adminDeactivationReason}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Verification Status Card */}
        {!isDeactivated && (!isVerified || showVerifiedBanner) && (
          <div className="bg-white border-2 p-8 md:p-10 mb-8 relative" style={{ borderColor: BRAND_COLORS.primary }}>
            {!isVerified ? (
              <div className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 border-2 rounded-full flex items-center justify-center" style={{ borderColor: BRAND_COLORS.accent }}>
                    <Clock className="w-8 h-8" style={{ color: BRAND_COLORS.accent }} />
                  </div>
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-light tracking-wide mb-4" style={{ color: BRAND_COLORS.primary }}>
                    Verification in Progress
                  </h2>
                  <p className="text-gray-600 font-light leading-relaxed mb-4">
                    Thank you for completing your business profile! Our team is currently reviewing and verifying
                    your information to ensure the safety and quality of our platform.
                  </p>
                  <p className="text-gray-600 font-light leading-relaxed mb-4">
                    This verification process typically takes 24-48 hours. We will notify you via SMS and in-app
                    notification once your account has been verified and you can start posting job opportunities.
                  </p>
                  <div className="p-4 border-2 bg-gray-50 mt-6" style={{ borderColor: BRAND_COLORS.lightGray }}>
                    <p className="text-sm font-light text-gray-600">
                      <span className="font-normal" style={{ color: BRAND_COLORS.primary }}>What's next?</span>
                      <br />
                      Once verified, you'll be able to:
                    </p>
                    <ul className="mt-3 space-y-2 text-sm font-light text-gray-600 ml-4">
                      <li>• Post job opportunities and requirements</li>
                      <li>• Browse and search for skilled professionals</li>
                      <li>• Manage job postings and applications</li>
                      <li>• Communicate with professionals</li>
                      <li>• Track bookings and payments</li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <button
                  onClick={handleDismissVerifiedBanner}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Dismiss"
                >
                  <X size={24} />
                </button>
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 border-2 rounded-full flex items-center justify-center" style={{ borderColor: BRAND_COLORS.accent }}>
                      <CheckCircle className="w-8 h-8" style={{ color: BRAND_COLORS.accent }} />
                    </div>
                  </div>
                  <div className="flex-1 pr-8">
                    <h2 className="text-2xl font-light tracking-wide mb-4" style={{ color: BRAND_COLORS.primary }}>
                      Account Verified!
                    </h2>
                    <p className="text-gray-600 font-light leading-relaxed">
                      Your business account has been successfully verified. You can now post job opportunities and
                      connect with skilled professionals.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Posted Jobs Section */}
        {isVerified && (
          <div className="mt-12">
            <h2 className="text-2xl font-light tracking-wide mb-6" style={{ color: BRAND_COLORS.primary }}>
              Your Job Postings
            </h2>

            {jobs.filter(job => job.status !== 'completed').length === 0 ? (
              <div className="bg-white border border-dashed border-gray-300 p-12 text-center rounded-lg">
                <Briefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No active jobs</h3>
                <p className="text-gray-500 font-light mb-6">Get started by posting your first job opportunity.</p>
                <button
                  onClick={() => setShowCreateJob(true)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Post a Job
                </button>
              </div>
            ) : (
              <div className="grid gap-6">
                {jobs.filter(job => job.status !== 'completed').map((job) => (
                  <div key={job.id} className="bg-white border border-gray-200 p-6 rounded-lg hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-medium text-gray-900 mb-1">{job.title}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Calendar size={14} className="mr-1" />
                            {job.date}
                          </span>
                          <span className="flex items-center">
                            <Clock size={14} className="mr-1" />
                            {job.time} ({job.duration}h)
                          </span>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize
                        ${job.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          job.status === 'allocated' ? 'bg-blue-100 text-blue-800' :
                            job.status === 'completed' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'}`}>
                        {job.status}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{job.description}</p>
                    <div className="flex justify-between items-center text-sm">
                      <div className="text-gray-500">
                        Budget: ₹{job.budget?.value}
                      </div>
                      <button
                        onClick={() => setSelectedJob(job)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </>
    );
  };

  const renderProfile = () => {
    return (
      <>
        <div className="mb-12">
          <h1 className="text-4xl font-light tracking-wide mb-2" style={{ color: BRAND_COLORS.primary }}>
            Business Profile
          </h1>
          <p className="text-gray-600 font-light">View and manage your business information</p>
        </div>

        {/* Business Summary */}
        {employerData && (
          <div className="bg-white border-2 p-8 md:p-10" style={{ borderColor: BRAND_COLORS.primary }}>
            <h3 className="text-xl font-light tracking-wide mb-6" style={{ color: BRAND_COLORS.primary }}>
              Business Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-light tracking-widest mb-2 uppercase text-gray-500">Business Name</p>
                <p className="font-light" style={{ color: BRAND_COLORS.primary }}>
                  {employerData.displayName || 'Not provided'}
                </p>
              </div>
              <div>
                <p className="text-xs font-light tracking-widest mb-2 uppercase text-gray-500">Location</p>
                <p className="font-light" style={{ color: BRAND_COLORS.primary }}>
                  {employerData.address?.city || 'Not provided'}
                </p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs font-light tracking-widest mb-2 uppercase text-gray-500">Full Address</p>
                <p className="font-light" style={{ color: BRAND_COLORS.primary }}>
                  {employerData.address?.addressLine}, {employerData.address?.city}, {employerData.address?.state} - {employerData.address?.pincode}
                </p>
              </div>
            </div>

            {/* Delete Account Button */}
            <div className="mt-8 pt-6 border-t border-gray-100">
              <button
                onClick={handleDeleteAccount}
                className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-md hover:bg-red-100 transition-colors text-sm font-medium"
              >
                Delete Account
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: BRAND_COLORS.light }}>
        <p className="text-gray-600 font-light">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: BRAND_COLORS.light }}>
      <Header
        user={user}
        userProfile={userProfile}
        currentView={currentView}
        onViewChange={setCurrentView}
      />
      <div className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {renderContent()}

          {/* Create Job Modal */}
          {showCreateJob && (
            <CreateJobForm
              onSubmit={handleCreateJob}
              onCancel={() => setShowCreateJob(false)}
              loading={creatingJob}
              employerData={employerData}
            />
          )}

          {/* Job Details Modal */}
          {selectedJob && (
            <EmployerJobDetails
              job={selectedJob}
              onClose={() => setSelectedJob(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployerHomePage;
